import IngredientDisplay from "@/models/Ingredient/IngredientDisplay";
import { Recipe } from "@/models/Recipes/Recipe";
import { RecipeCompositionMeasurement } from "@/models/Recipes/RecipeCompositionMeasurement";
import { RecipeIngredient } from "@/models/Recipes/RecipeIngredient";
import { Subrecipe } from "@/models/Recipes/Subrecipe";
import { groupBy } from "@/utils/Helpers";
import {Ingredient} from "@/models/Ingredient/Ingredient";
import {DishRecipeCommon} from "@/models/DishRecipeCommon";

export function getSubrecipeWeightInGrams(subrecipe: Subrecipe, subrecipeMeasurements: RecipeCompositionMeasurement[]) {
  const measurement = subrecipeMeasurements.find(
    (x) => x.id == subrecipe.recipeCompositionMeasurementId
  );

  let subRecipeWeight = subrecipe.quantity * subrecipe.weightPerServing;

  if (measurement && measurement.measurement.name.toLowerCase() !== "serving") {
    subRecipeWeight = subrecipe.quantity * measurement.measurement.conversionCoeficient;
  }

  return subRecipeWeight;
}

export function getIngredientDisplays(
  ingredients: RecipeIngredient[],
  savedDisplays: IngredientDisplay[],
  quantityMultiplier: number = 1
): IngredientDisplay[] {
  // check if there is a recipe
  // if not, will return empty aRRAY
  const displays: IngredientDisplay[] = [];

  // map the ingredients
  ingredients.map((ingr) => {
    // See if you can find savedDisplay in savedDisplays for the ingredient Id
    const savedDisplay = savedDisplays.find(
      (x) => x.ingredientId == ingr.ingredient.id
    );

    const ingrWeight = quantityMultiplier * ingr.quantity * ingr.measurement.weightInGrams;

    // If found, push that one
    if (savedDisplay) {
      displays.push({
        ingredientId: savedDisplay.ingredientId,
        displayTextInPrimaryLanguage: savedDisplay.displayTextInPrimaryLanguage,
        displayTextInSecondaryLanguage: savedDisplay.displayTextInSecondaryLanguage,
        isAllergen: savedDisplay.isAllergen,
        allergenFormattingStyle: savedDisplay.allergenFormattingStyle,
        allergenFormattingColor: savedDisplay.allergenFormattingColor || "#C62828",
        allergenFormattingHighlightColor: savedDisplay.allergenFormattingHighlightColor || "#FFF176",
        allergenFormattingFontFamily: savedDisplay.allergenFormattingFontFamily || "Arial",
        allergenFormattingFontSize: savedDisplay.allergenFormattingFontSize || "7pt",
        isBranded: ingr.ingredient.components != null,
        weightInGrams: ingrWeight,
      });
    }

    else {
      let displayTextInPrimaryLanguage = buildSimpleIngredientName(ingr.ingredient);

      displays.push({
        ingredientId: ingr.ingredient.id,
        displayTextInPrimaryLanguage: displayTextInPrimaryLanguage,
        displayTextInSecondaryLanguage: "",
        isAllergen: false,
        allergenFormattingStyle: "bold",
        allergenFormattingColor: "#C62828",
        allergenFormattingHighlightColor: "#FFF176",
        allergenFormattingFontFamily: "Arial",
        allergenFormattingFontSize: "7pt",
        isBranded: ingr.ingredient.components != null,
        weightInGrams: ingrWeight,
      });
    }
  });

  return displays;
}

function getSubrecipeDisplayAsIngredients(
  subrecipe: Subrecipe,
  savedDisplays: IngredientDisplay[],
  subrecipeMeasurements: RecipeCompositionMeasurement[],
  quantityMultiplier: number = 1
) {
  const subrecipeRawWeight = subrecipe.recipe.rawWeight;
  const subrecipeProduces = subrecipe.recipe.rawWeightPerServing;

  const ingredientScaleFactor = (subrecipeProduces / subrecipeRawWeight) * quantityMultiplier;

  let displays: IngredientDisplay[] = getIngredientDisplays(
    subrecipe.recipe.ingredients,
    savedDisplays,
    subrecipe.quantity * ingredientScaleFactor
  );

  for (const childRecipe of subrecipe.recipe.childRecipes) {
    const fetchedDisplays = getSubrecipeDisplayAsIngredients(
      childRecipe,
      savedDisplays,
      subrecipeMeasurements,
      childRecipe.quantity * ingredientScaleFactor
    );

    displays = displays.concat(fetchedDisplays);
  }

  return displays;
}

export function getIngredientAndSubRecipeDisplays(
  ingredientDisplays: IngredientDisplay[],
  recipe: DishRecipeCommon,
  subRecipeDisplays: IngredientDisplay[],
  subrecipeMeasurements: RecipeCompositionMeasurement[],
  renderExpandedSubrecipes: boolean = false
): IngredientDisplay[] {
  let preprocessedDisplays: IngredientDisplay[] = ingredientDisplays;

  if (renderExpandedSubrecipes) {
    // Prep the subrecipes to be displayed as ingredients
    for (const subrecipe of recipe.childRecipes) {
      //const subrecipeWeight = getSubrecipeWeightInGrams(subrecipe, subrecipeMeasurements);
      //const subrecipeWeightAsServings = subrecipeWeight / subrecipe.weightPerServing;

      ingredientDisplays = ingredientDisplays.concat(
        getSubrecipeDisplayAsIngredients(
          subrecipe,
          ingredientDisplays,
          subrecipeMeasurements,
          1
        )
      );
    }

    // Group the displays by ingredientId so that we can sum the weights
    const groupedDisplays = groupBy(ingredientDisplays, (x) => x.ingredientId.toString());
    const rv: IngredientDisplay[] = [];

    // Sum the weights of the grouped displays
    for (const key of groupedDisplays.keys()) {
      const ingredientDisplays: IngredientDisplay[] = groupedDisplays.get(key);

      if (ingredientDisplays) {
        const totalWeight = ingredientDisplays.reduce(
          (acc, x) => acc + x.weightInGrams,
          0
        );

        // Clone the first display of the group to not accumulate the weight over multiple displays
        const display = structuredClone(ingredientDisplays[0]);
        display.weightInGrams = totalWeight;
        rv.push(display);
      }
    }

    return rv;
  }

  else {
    return preprocessedDisplays.concat(subRecipeDisplays);
  }
}

/**
 * Constructs a simple ingredient name based on the provided ingredient object.
 * If the ingredient has sub-ingredients, they are concatenated to the name.
 * If the ingredient has components, those will be used as the name instead.
 *
 * @param {Ingredient} ingredient - The ingredient object containing name, sub-ingredients, and components.
 * @return {string} The constructed ingredient name.
 */
export function buildSimpleIngredientName(ingredient: Ingredient): string {
  let rv = ingredient.name;

  // If a custom ingredient has sub ingredients, concat them to the name
  if (ingredient.subIngredients) {
    rv += ` (${ingredient.subIngredients})`;
  }

  // Case for standardized ingredients
  if (ingredient.components) {
    rv = ingredient.components;
  }

  return rv;
}

/**
 * Builds a complex ingredient name from a recipe and its sub-recipes, including ingredient details,
 * child recipes, and nested child recipes.
 *
 * @param {Recipe} recipe - The recipe object containing a name, ingredients, and child recipes to construct the ingredient name from.
 * @return {string} The fully constructed complex name including the recipe name, its ingredients, and names of sub-recipes and their ingredients.
 */
export function buildComplexIngredientName(recipe: Recipe): string {
  // First level
  let rv = recipe.name;

  if (recipe.ingredients.length > 0) {
    // Concat the subRecipe's ingredients
    const firstLevelSubRecipe = _subRecipeIngredients(
      recipe.ingredients
    );

    rv += " (" + firstLevelSubRecipe.join(", ");
  }

  // Second level
  recipe.childRecipes.forEach((childRecipe) => {
    rv += (
      (recipe.ingredients.length > 0 ? ", " : " (") + childRecipe.recipe.name
    );

    if (childRecipe.recipe.ingredients.length > 0) {
      // Concat the subRecipe's child ingredients
      const secondLevelSubRecipe = _subRecipeIngredients(
        childRecipe.recipe.ingredients
      );

      rv += " (" + secondLevelSubRecipe.join(", ");
    }

    // Third level: Concat the subRecipe's child -> child recipes only by name
    childRecipe.recipe.childRecipes.forEach((thirdLevelChildRecipe) => {
      rv += (
        (childRecipe.recipe.ingredients.length > 0 ? ", " : " (") + thirdLevelChildRecipe.recipe.name
      );
    });

    rv += ")";
  });
  rv += ")";
  return rv;
}

/**
 * Processes a list of recipe ingredients, sorts them by their weight contribution,
 * and returns their names in order of importance.
 *
 * @param {RecipeIngredient[]} ingredients - Array of ingredients to be processed, where each ingredient includes its name, quantity, and measurement details.
 * @return {string[]} Sorted list of ingredient names based on their weight contribution in the recipe.
 */
function _subRecipeIngredients(ingredients: RecipeIngredient[]): string[] {
  // Sort ingredients by their quantity contribution in the subRecipe
  ingredients.sort(
    (firstIngredient, secondIngredient) =>
      (secondIngredient.quantity * secondIngredient.measurement.weightInGrams) - (firstIngredient.quantity * firstIngredient.measurement.weightInGrams)
  );

  const subRecipeIngredients: string[] = [];
  ingredients.forEach((ingredient) => {
    subRecipeIngredients.push(ingredient.ingredient.name);
  });

  return subRecipeIngredients;
}
